import { useState } from 'react'
import {
  Card, Table, Button, Space, Typography, Select, Tag,
  Drawer, Form, Input, message, Row, Col, Tabs, Switch, DatePicker, InputNumber,
} from 'antd'
import { KeyOutlined, PlusOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title } = Typography
const API = ''

const ISSUE_STATUS_COLORS: Record<string, string> = {
  ISSUED: 'orange',
  RETURNED: 'green',
  OVERDUE: 'red',
  LOST: 'error',
}

export default function KeyControlPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const qc = useQueryClient()
  const [activeTab, setActiveTab] = useState('issues')
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [issueDrawerOpen, setIssueDrawerOpen] = useState(false)
  const [keyDrawerOpen, setKeyDrawerOpen] = useState(false)
  const [issueForm] = Form.useForm()
  const [keyForm] = Form.useForm()

  const { data: keyMaster, isLoading: keyMasterLoading } = useQuery({
    queryKey: ['key-master', plantId],
    queryFn: () =>
      apiClient.get(`${API}/key-master`, { params: { plant_id: plantId } }).then(r => r.data),
    enabled: !!plantId,
  })

  const { data: keyIssues, isLoading: keyIssuesLoading } = useQuery({
    queryKey: ['key-issues', plantId, statusFilter],
    queryFn: () =>
      apiClient.get(`${API}/key-issues`, {
        params: { plant_id: plantId, status: statusFilter || undefined },
      }).then(r => r.data),
    enabled: !!plantId,
  })

  const createKeyMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/key-master`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['key-master'] })
      message.success('Key added to master')
      setKeyDrawerOpen(false)
      keyForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const issueKeyMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post(`${API}/key-issues`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['key-issues'] })
      message.success('Key issued')
      setIssueDrawerOpen(false)
      issueForm.resetFields()
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const returnMut = useMutation({
    mutationFn: (id: string) =>
      apiClient.post(`${API}/key-issues/${id}/return`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['key-issues'] })
      message.success('Key returned')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const masterList = Array.isArray(keyMaster) ? keyMaster : []
  const issueList = Array.isArray(keyIssues) ? keyIssues : []

  const issueColumns = [
    {
      title: 'Event No',
      dataIndex: 'key_event_no',
      key: 'key_event_no',
      width: 120,
      render: (v: string) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Key',
      key: 'key_desc',
      width: 180,
      render: (_: unknown, r: any) => r.key?.key_description || r.key_id || '—',
    },
    { title: 'Issued To', dataIndex: 'issued_to', key: 'issued_to', width: 140 },
    { title: 'Department', dataIndex: 'department', key: 'department', width: 120 },
    {
      title: 'Issue Time',
      dataIndex: 'issue_time',
      key: 'issue_time',
      width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Due Return',
      dataIndex: 'due_return_time',
      key: 'due_return_time',
      width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Actual Return',
      dataIndex: 'actual_return_time',
      key: 'actual_return_time',
      width: 130,
      render: (v: string) => v ? dayjs(v).format('DD-MMM HH:mm') : '—',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (v: string) => <Tag color={ISSUE_STATUS_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 110,
      render: (_: unknown, r: any) =>
        (r.status === 'ISSUED' || r.status === 'OVERDUE') ? (
          <Button
            size="small"
            type="primary"
            loading={returnMut.isPending}
            onClick={() => returnMut.mutate(r.id)}
          >
            Return Key
          </Button>
        ) : null,
    },
  ]

  const masterColumns = [
    { title: 'Key Code', dataIndex: 'key_code', key: 'key_code', width: 120 },
    { title: 'Description', dataIndex: 'key_description', key: 'key_description' },
    { title: 'Area', dataIndex: 'area', key: 'area', width: 140 },
    { title: 'Total Copies', dataIndex: 'total_copies', key: 'total_copies', width: 110, align: 'center' as const },
    {
      title: 'Sensitive',
      dataIndex: 'is_sensitive',
      key: 'is_sensitive',
      width: 100,
      render: (v: boolean) => <Tag color={v ? 'red' : 'default'}>{v ? 'YES' : 'NO'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, r: any) => (
        <Button
          size="small"
          onClick={() => {
            issueForm.setFieldValue('key_id', r.id)
            setIssueDrawerOpen(true)
          }}
        >
          Issue
        </Button>
      ),
    },
  ]

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <KeyOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Key Control
      </Title>
      <Typography.Text type="secondary">Key master and issue/return tracking</Typography.Text>

      <Card style={{ marginTop: 16 }}>
        <Row gutter={12} style={{ marginBottom: 8 }}>
          <Col>
            <Select
              value={plantId}
              onChange={setPlantId}
              style={{ width: 200 }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))}
            />
          </Col>
        </Row>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'issues', label: 'Key Issues' },
            { key: 'master', label: 'Key Master' },
          ]}
        />

        {activeTab === 'issues' && (
          <>
            <Row gutter={12} style={{ marginBottom: 16 }} justify="space-between">
              <Col>
                <Select
                  allowClear
                  placeholder="Filter by Status"
                  style={{ width: 150 }}
                  value={statusFilter}
                  onChange={v => setStatusFilter(v)}
                  options={['ISSUED', 'RETURNED', 'OVERDUE', 'LOST'].map(s => ({ value: s, label: s }))}
                />
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => { issueForm.resetFields(); setIssueDrawerOpen(true) }}
                >
                  Issue Key
                </Button>
              </Col>
            </Row>
            <Table
              dataSource={issueList}
              columns={issueColumns}
              rowKey="id"
              loading={keyIssuesLoading}
              size="small"
              pagination={{ pageSize: 50 }}
              scroll={{ x: 1100 }}
            />
          </>
        )}

        {activeTab === 'master' && (
          <>
            <Row justify="end" style={{ marginBottom: 16 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => { keyForm.resetFields(); setKeyDrawerOpen(true) }}
              >
                Add Key
              </Button>
            </Row>
            <Table
              dataSource={masterList}
              columns={masterColumns}
              rowKey="id"
              loading={keyMasterLoading}
              size="small"
              pagination={{ pageSize: 50 }}
            />
          </>
        )}
      </Card>

      {/* Issue Key Drawer */}
      <Drawer
        title="Issue Key"
        open={issueDrawerOpen}
        onClose={() => { setIssueDrawerOpen(false); issueForm.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setIssueDrawerOpen(false); issueForm.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={issueKeyMut.isPending}
                onClick={() =>
                  issueForm.validateFields().then(vals => {
                    const payload = {
                      ...vals,
                      plant_id: plantId,
                      due_return_time: vals.due_return_time
                        ? vals.due_return_time.toISOString()
                        : undefined,
                    }
                    issueKeyMut.mutate(payload)
                  })
                }
              >
                Issue
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={issueForm} layout="vertical">
          <Form.Item name="key_id" label="Key" rules={[{ required: true }]}>
            <Select
              options={masterList.map((k: any) => ({
                value: k.id,
                label: `${k.key_code} — ${k.key_description}`,
              }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item name="issued_to" label="Issued To" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="department" label="Department">
            <Input />
          </Form.Item>
          <Form.Item name="purpose" label="Purpose">
            <Input />
          </Form.Item>
          <Form.Item name="due_return_time" label="Due Return Time">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Drawer>

      {/* Key Master Drawer */}
      <Drawer
        title="Add Key to Master"
        open={keyDrawerOpen}
        onClose={() => { setKeyDrawerOpen(false); keyForm.resetFields() }}
        width={440}
        footer={
          <Row justify="end">
            <Space>
              <Button onClick={() => { setKeyDrawerOpen(false); keyForm.resetFields() }}>Cancel</Button>
              <Button
                type="primary"
                loading={createKeyMut.isPending}
                onClick={() =>
                  keyForm.validateFields().then(vals =>
                    createKeyMut.mutate({ ...vals, plant_id: plantId })
                  )
                }
              >
                Create
              </Button>
            </Space>
          </Row>
        }
      >
        <Form form={keyForm} layout="vertical" initialValues={{ is_sensitive: false, total_copies: 1 }}>
          <Form.Item name="key_code" label="Key Code" rules={[{ required: true }]}>
            <Input placeholder="e.g. KEY-001" />
          </Form.Item>
          <Form.Item name="key_description" label="Key Description" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="area" label="Area">
            <Input placeholder="e.g. Godown A" />
          </Form.Item>
          <Form.Item name="total_copies" label="Total Copies">
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="is_sensitive" label="Sensitive?" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}
